import { Trash2 } from "lucide-react";
import type { FC } from "react";
import { useParams } from "react-router-dom";

import {
  type AssignedRole,
  useUnassignmentUserMutation,
} from "@/api/admin/users";
import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import { BasicModal, toastError, toastSuccess } from "@/components/molecules";
import { commonIconProps12 } from "@/utils/constants";
import { formatEnumValue } from "@/utils/formatEnumValue";

interface UnassignRoleProps {
  data: AssignedRole;
  userName: string;
}

export const UnassignRole: FC<UnassignRoleProps> = ({ data, userName }) => {
  const { mutate, isPending } = useUnassignmentUserMutation();
  const { id = "" } = useParams();

  const Trigger = () => {
    return (
      <div className="flex items-center gap-2">
        <Trash2 {...commonIconProps12} />
        <span className="text-xs">Unassign from role</span>
      </div>
    );
  };

  const handleConfirm = () => {
    mutate(data.id, {
      onSuccess: () => {
        toastSuccess("User was unassigned successfully!");
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.adminUserRole, id],
        });
      },
      onError: () => {
        toastError(
          "Oops! Something went wrong.",
          () => handleConfirm(),
          "Error"
        );
      },
    });
  };

  return (
    <BasicModal
      trigger={<Trigger />}
      title="Unassign user"
      body={
        <p className="text-sm text-gray-950 mb-2">
          Are you sure you want to Unassign
          <span className="font-bold pl-1">{userName}</span> from the
          <span className="px-1">{formatEnumValue(data.role)}</span>
          role?
        </p>
      }
      action={handleConfirm}
      btnTitle="Unassign"
      isPending={isPending}
      classes="cursor-pointer px-2 py-1.5 w-full rounded-[6px] focus:bg-accent hover:bg-accent focus:text-accent-foreground"
    />
  );
};
